import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Alert,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

interface DataExplanationDialogProps {
  open: boolean;
  onClose: () => void;
  explanation: string | null | any;
  isLoading: boolean;
  title?: string;
  dailyLimitInfo?: {
    dailyMessageCount: number;
    dailyLimit: number;
    isLimitReached: boolean;
  } | null;
}

export const DataExplanationDialog: React.FC<DataExplanationDialogProps> = ({
  open,
  onClose,
  explanation,
  isLoading,
  title = "Data Analysis Insights",
  dailyLimitInfo,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" component="h2">
            {title}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {isLoading ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            py={4}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Analyzing data...
            </Typography>
          </Box>
        ) : explanation ? (
          <Typography
            variant="body1"
            sx={{
              whiteSpace: "pre-wrap",
              lineHeight: 1.6,
              fontSize: "0.95rem",
              fontFamily: "inherit", // Use system font for better readability
            }}
          >
            {typeof explanation === "string"
              ? explanation
              : JSON.stringify(explanation, null, 2)}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No explanation available.
          </Typography>
        )}

        {/* Daily limit information */}
        {dailyLimitInfo && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
            {dailyLimitInfo.isLimitReached ? (
              <Alert severity="warning" sx={{ mb: 1 }}>
                Daily explanation limit reached (
                {dailyLimitInfo.dailyMessageCount}/{dailyLimitInfo.dailyLimit}).
                Please try again tomorrow or upgrade your plan.
              </Alert>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Daily explanations used: {dailyLimitInfo.dailyMessageCount}/
                {dailyLimitInfo.dailyLimit}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
